import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import every from 'lodash/every';
import Loader from '../../common/loader';
import { getMailingAddress } from '../user-profile/selectors';
import {
  getCustomerParty,
  getPolicyEmail,
  getPolicyMailingAddress,
  getStatementAddress,
  getStatementEmailAddress,
} from './selectors';
import {
  ArrowDownIcon,
  ArrowRightIcon,
  GoPaperlessContainer,
  GoPaperlessPolicyPreferenceContainer,
  LoaderContainer,
  PolicySummary,
  PolicyLabelMobile,
  PolicyEmailAddress,
  PolicyMailingAddress,
  PolicyNumberLabel,
  PreferenceLabelMobile,
  PreferenceRadioEmail,
  PreferenceRadioMail,
  ProfileEditorsContainer,
  ProfileRadioButtonContainer,
  OnlineProfileHeader,
  SaveCancelContainer,
  CancelButton,
  SaveDeliveryPreferencesButton,
  StatementPreferenceInfo,
  EditDeliveryPreferencesButton,
} from './styles';
import { getText } from '../../../utils/i18n';
import {
  saveCustomerDeliveryPreferences,
  updateMultipleDeliveryPreferences,
} from '../user-profile/profileAPI';
import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router';
import { getStatementAccounts, updateStatementAccounts } from './profile-api';
import { setLoading } from './deliveryPreferencesReducer';

export const DeliveryPreferences = (props) => {
  const {
    i18n,
    billingAccounts,
    customer,
    customerAddress,
    customerEmail,
    customerId,
    createdSinceDate,
    desktopDeliveryPreferencesDefaultExpanded,
    mobileDeliveryPreferencesDefaultExpanded,
    policiesCountLimit,
    policyStatusCd,
    systemId,
    dispatch,
    isLoading,
    policies,
    statementAccounts,
  } = props;
  let location = useLocation();
  const [deliveryPreferencesOpen, setDeliveryPreferencesOpen] = useState(
    window.innerWidth > 900
      ? desktopDeliveryPreferencesDefaultExpanded
      : mobileDeliveryPreferencesDefaultExpanded,
  );
  const [editingDeliveryPreferences, setEditingDeliveryPreferences] = useState(false);
  const [customerDeliveryPreference, setCustomerDeliveryPreference] = useState(
    customer.preferredDeliveryMethod,
  );

  const [statementAccountDeliveryPreference, setStatementAccountDeliveryPreference] = useState([]);

  let filteredStatementAccounts = billingAccounts.filter((account) => {
    return account.fullAccount.accountDisplayName === 'Statement';
  });
  let filteredUnderlyingAccounts = filteredStatementAccounts.map((thisAccount) => {
    return thisAccount.fullAccount.underlyingBillingAccounts.filter((account) => {
      return account.accountDisplayName !== 'Statement';
    });
  });
  let policiesMapped = policies.map((policy) => {
    return { ...policy, newPreference: policy?.insured?.preferredDeliveryMethod };
  });
  const [mappedPolicyPreference, setMappedPolicyPreference] = useState(policiesMapped);
  const { enqueueSnackbar } = useSnackbar();
  const goPaperlessMissingEmailHelperText = getText(
    i18n,
    'policyDetails.helper.missingEmailText',
    'No email address found. Please add an email address to your policy if you wish to select paperless.',
  );

  useEffect(() => {
    dispatch(setLoading(true));
    setTimeout(() => {
      if (location?.state?.prevPath === 'policyDetails') {
        document
          .getElementById('GoPaperlessContainer')
          .scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 1000);
    let filteredStatementAccounts = billingAccounts.filter((account) => {
      return account.fullAccount.accountDisplayName === 'Statement';
    });

    dispatch(
      getStatementAccounts(filteredStatementAccounts, setStatementAccountDeliveryPreference),
    );
  }, []);

  useEffect(() => {
    setCustomerDeliveryPreference(customer.preferredDeliveryMethod);
  }, [customer]);

  useEffect(() => {
    let policiesMappedAfterChange = policies.map((policy) => {
      return { ...policy, newPreference: policy.insured.preferredDeliveryMethod };
    });
    setMappedPolicyPreference(policiesMappedAfterChange);
  }, [policies]);

  let updateDeliveryPreferences = async () => {
    let filteredPoliciesToUpdate = mappedPolicyPreference.filter((policy) => {
      let currentPolicy = policies.find((thisPolicy) => policy.policyRef === thisPolicy.policyRef);
      return policy.newPreference !== currentPolicy.insured.preferredDeliveryMethod;
    });
    let filteredStatementAccountsToUpdate = statementAccountDeliveryPreference.filter((policy) => {
      let currentPolicy = statementAccounts.find(
        (thisPolicy) => thisPolicy.statementFeeAccountRef === policy.systemId,
      );
      return policy.deliveryPreference !== currentPolicy.preferredDeliveryMethod;
    });
    if (customer.preferredDeliveryMethod !== customerDeliveryPreference) {
      await dispatch(
        saveCustomerDeliveryPreferences(
          systemId,
          customer._revision,
          customerDeliveryPreference,
          enqueueSnackbar,
        ),
      );
    }
    if (filteredStatementAccountsToUpdate.length > 0) {
      let underlyingStatementAccountPoliciesAndOtherPolicies = [];
      filteredStatementAccountsToUpdate.forEach((statementAccount, index) => {
        let statementBillingAccount = billingAccounts.find((account) => {
          return account.fullAccount.accountDisplayNumber === statementAccount.accountDisplayNumber;
        });

        statementBillingAccount.fullAccount.underlyingBillingAccounts.forEach((account) => {
          if (account.arPolicy !== undefined) {
            let nonStatementPolicy = {
              ...policies.find((policy) => {
                return policy.policyRef === account.arPolicy.policyRef;
              }),
              newPreference: statementAccount.deliveryPreference,
            };
            underlyingStatementAccountPoliciesAndOtherPolicies.push(nonStatementPolicy);
          }
        });
      });
      filteredPoliciesToUpdate.forEach((policy) => {
        underlyingStatementAccountPoliciesAndOtherPolicies.push(policy);
      });
      dispatch(
        updateStatementAccounts(
          filteredStatementAccountsToUpdate,
          statementAccounts,
          enqueueSnackbar,
        ),
      );
      dispatch(
        updateMultipleDeliveryPreferences(
          mappedPolicyPreference,
          underlyingStatementAccountPoliciesAndOtherPolicies,
          enqueueSnackbar,
          setMappedPolicyPreference,
          customerId,
          createdSinceDate,
          policiesCountLimit,
          policyStatusCd,
        ),
      );
      dispatch(
        getStatementAccounts(filteredStatementAccounts, setStatementAccountDeliveryPreference),
      );
    } else {
      await dispatch(
        updateMultipleDeliveryPreferences(
          mappedPolicyPreference,
          filteredPoliciesToUpdate,
          enqueueSnackbar,
          setMappedPolicyPreference,
          customerId,
          createdSinceDate,
          policiesCountLimit,
          policyStatusCd,
        ),
      );
    }
  };
  const updatePreferences = (index, option) => {
    let newMap = mappedPolicyPreference.map((policy, i) => {
      if (i === index) {
        policy.newPreference = option;
        return policy;
      } else {
        return policy;
      }
    });
    setMappedPolicyPreference(newMap);
  };
  const checkDisabled = () => {
    if (!editingDeliveryPreferences) {
      return true;
    }
    let filteredStatementAccountsMappedPolicyPreference = mappedPolicyPreference.filter(
      (policy) => {
        return !filteredUnderlyingAccounts.some((account) => {
          return account.some((subAccount) => {
            subAccount.arPolicy.policyRef === policy.policyRef;
          });
        });
      },
    );
    let policiesAndCustomerCommunicationPreferencesNotChanged = every(
      filteredStatementAccountsMappedPolicyPreference,
      (policy) => {
        let currentPolicy = policies.find(
          (thisPolicy) => policy.policyRef === thisPolicy.policyRef,
        );
        return (
          currentPolicy.insured.preferredDeliveryMethod === policy.newPreference &&
          customerDeliveryPreference === customer.preferredDeliveryMethod
        );
      },
    );
    let statementAccountCommunicationPreferencesNotChanged = every(statementAccounts, (account) => {
      let matched = statementAccountDeliveryPreference.find(
        (thisAccount) => thisAccount.systemId === account?.statementFeeAccountRef,
      );
      return matched.deliveryPreference === account.preferredDeliveryMethod;
    });

    return (
      policiesAndCustomerCommunicationPreferencesNotChanged &&
      statementAccountCommunicationPreferencesNotChanged
    );
  };
  return (
    <GoPaperlessContainer>
      <label htmlFor="deliveryPreferencesHeader" style={{ cursor: 'pointer' }}>
        <OnlineProfileHeader
          id="deliveryPreferencesHeader"
          onClick={() => setDeliveryPreferencesOpen(!deliveryPreferencesOpen)}
        >
          {deliveryPreferencesOpen ? <ArrowDownIcon /> : <ArrowRightIcon />} Delivery Preferences
        </OnlineProfileHeader>
      </label>
      {isLoading && (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      )}
      {deliveryPreferencesOpen && !isLoading && (
        <>
          <p
            dangerouslySetInnerHTML={{
              __html: getText(
                i18n,
                'general.paperlessSubtitle',
                'Enjoy convenient delivery of your policy-related documents to your email.',
              ),
            }}
          />
          <br />
          {mappedPolicyPreference.map((policy, index) => {
            return (
              !filteredUnderlyingAccounts.some((account) =>
                account.some((subAccount) => subAccount.arPolicy.policyRef === policy.policyRef),
              ) && (
                <GoPaperlessPolicyPreferenceContainer key={policy.policyRef} index={index}>
                  <PolicySummary>
                    <PolicyLabelMobile>
                      Policy {policy.policyNumber + ' (' + policy.statusCd + ')'}
                    </PolicyLabelMobile>
                    <PolicyNumberLabel
                      aria-label={`Policy Number ${policy.policyNumber} Status ${policy.statusCd}`}
                    >
                      {policy.policyNumber + ' (' + policy.statusCd + ')'}
                    </PolicyNumberLabel>
                    <PolicyEmailAddress>
                      Email:{' '}
                      {getPolicyEmail(policy)
                        ? getPolicyEmail(policy)
                        : goPaperlessMissingEmailHelperText}
                    </PolicyEmailAddress>
                    <PolicyMailingAddress>
                      Address: {getPolicyMailingAddress(policy)}
                    </PolicyMailingAddress>
                  </PolicySummary>
                  <ProfileEditorsContainer>
                    <PreferenceLabelMobile>Preference</PreferenceLabelMobile>
                    <ProfileRadioButtonContainer>
                      <PreferenceRadioEmail>
                        <label htmlFor={`radioEmailPolicies${index}`}>
                          <input
                            aria-label={`Preference for ${policy.policyNumber} Paperless`}
                            type="radio"
                            name={'deliveryPref_policy_email' + index}
                            value="Email"
                            id={`radioEmailPolicies${index}`}
                            onChange={() => updatePreferences(index, 'Email')}
                            checked={
                              policy.newPreference === null
                                ? policy.insured.preferredDeliveryMethod === 'Email'
                                : policy.newPreference === 'Email'
                            }
                            disabled={
                              isLoading || !editingDeliveryPreferences || !getPolicyEmail(policy)
                            }
                          />{' '}
                          Paperless
                        </label>
                      </PreferenceRadioEmail>
                      <PreferenceRadioMail>
                        <label htmlFor={`radioBatchPolicies${index}`}>
                          <input
                            aria-label={`Preference for ${policy.policyNumber} Mail`}
                            type="radio"
                            name={'deliveryPref_policy_batch' + index}
                            value="Batch"
                            id={`radioBatchPolicies${index}`}
                            onChange={() => updatePreferences(index, 'Batch')}
                            checked={
                              policy.newPreference === null
                                ? policy.insured.preferredDeliveryMethod === 'Batch'
                                : policy.newPreference === 'Batch'
                            }
                            disabled={isLoading || !editingDeliveryPreferences}
                          />{' '}
                          Mail
                        </label>
                      </PreferenceRadioMail>
                    </ProfileRadioButtonContainer>
                  </ProfileEditorsContainer>
                </GoPaperlessPolicyPreferenceContainer>
              )
            );
          })}
          {statementAccounts.map((policy, index) => {
            let currentStatementAccountPolicy = filteredStatementAccounts.find(
              (account) =>
                account.fullAccount.accountDisplayNumber === policy.statementAccountDisplayNumber,
            );
            let underlyingSAPolicyNames =
              currentStatementAccountPolicy.fullAccount.underlyingBillingAccounts.reduce(
                (acc, account) =>
                  account.accountDisplayName !== 'Statement'
                    ? `${account.arPolicy?.policyNumber} (${account.statusCd})${
                        acc ? ',' : ''
                      } ${acc}`
                    : acc,
                '',
              );
            return (
              <GoPaperlessPolicyPreferenceContainer key={index} index={index}>
                <PolicySummary>
                  <PolicyLabelMobile>{underlyingSAPolicyNames}</PolicyLabelMobile>
                  <PolicyNumberLabel>{underlyingSAPolicyNames}</PolicyNumberLabel>
                  <PolicyEmailAddress>
                    Email:{' '}
                    {getStatementEmailAddress(currentStatementAccountPolicy)
                      ? getStatementEmailAddress(currentStatementAccountPolicy)
                      : goPaperlessMissingEmailHelperText}
                  </PolicyEmailAddress>
                  <StatementPreferenceInfo>
                    Statement Accounts preferences will be applied to all underlying policies <br />
                  </StatementPreferenceInfo>
                  <PolicyMailingAddress>
                    Address: {getStatementAddress(currentStatementAccountPolicy)}
                  </PolicyMailingAddress>
                </PolicySummary>
                <ProfileEditorsContainer>
                  <PreferenceLabelMobile>Preference</PreferenceLabelMobile>
                  <ProfileRadioButtonContainer>
                    <PreferenceRadioEmail>
                      <label htmlFor={`radioEmailStatement${index}`}>
                        <input
                          aria-label={`Preference for statement account communications via email`}
                          type="radio"
                          name={`deliveryPref_statements_email${index}`}
                          value="Email"
                          id={`radioEmailStatement${index}`}
                          onChange={() =>
                            setStatementAccountDeliveryPreference((previousDeliveryPrefs) => {
                              return previousDeliveryPrefs.map((item) => {
                                return item.systemId === policy.statementFeeAccountRef
                                  ? { ...item, deliveryPreference: 'Email' }
                                  : item;
                              });
                            })
                          }
                          checked={
                            statementAccountDeliveryPreference.find((preference) => {
                              return policy.statementFeeAccountRef === preference.systemId;
                            })?.deliveryPreference === 'Email'
                          }
                          disabled={
                            isLoading ||
                            !editingDeliveryPreferences ||
                            !getStatementEmailAddress(currentStatementAccountPolicy)
                          }
                        />{' '}
                        Paperless
                      </label>
                    </PreferenceRadioEmail>
                    <PreferenceRadioMail>
                      <label htmlFor={`radioBatchStatement${index}`}>
                        <input
                          aria-label={`Preference for statement account communications via Mail`}
                          type="radio"
                          name={`deliveryPref_statements_batch${index}`}
                          value="Batch"
                          id={`radioBatchStatement${index}`}
                          onChange={() =>
                            setStatementAccountDeliveryPreference((previousDeliveryPrefs) => {
                              return previousDeliveryPrefs.map((item) => {
                                return item.systemId === policy.statementFeeAccountRef
                                  ? { ...item, deliveryPreference: 'Batch' }
                                  : item;
                              });
                            })
                          }
                          checked={
                            statementAccountDeliveryPreference.find((preference) => {
                              return policy.statementFeeAccountRef === preference.systemId;
                            })?.deliveryPreference === 'Batch'
                          }
                          disabled={isLoading || !editingDeliveryPreferences}
                        />{' '}
                        Mail
                      </label>
                    </PreferenceRadioMail>
                  </ProfileRadioButtonContainer>
                </ProfileEditorsContainer>
              </GoPaperlessPolicyPreferenceContainer>
            );
          })}
          <GoPaperlessPolicyPreferenceContainer>
            <PolicySummary>
              <PolicyLabelMobile>All Other Customer Communications</PolicyLabelMobile>
              <PolicyNumberLabel>All Other Customer Communications</PolicyNumberLabel>
              <PolicyEmailAddress>
                Email: {customerEmail ? customerEmail : goPaperlessMissingEmailHelperText}
              </PolicyEmailAddress>
              <PolicyMailingAddress>
                Address:{' '}
                {`${customerAddress.addr1} ${customerAddress.city} ${customerAddress.stateProvCd} ${customerAddress.postalCode}`}
              </PolicyMailingAddress>
            </PolicySummary>
            <ProfileEditorsContainer>
              <PreferenceLabelMobile>Preference</PreferenceLabelMobile>
              <ProfileRadioButtonContainer>
                <PreferenceRadioEmail>
                  <label htmlFor="radioEmailCustomer">
                    <input
                      aria-label={`Preference for all other Customer communications Paperless`}
                      type="radio"
                      name={'deliveryPref_customer_email'}
                      value="CustomerEmail"
                      id="radioEmailCustomer"
                      onChange={() => setCustomerDeliveryPreference('Email')}
                      checked={customerDeliveryPreference === 'Email'}
                      disabled={isLoading || !editingDeliveryPreferences || !customerEmail}
                    />{' '}
                    Paperless
                  </label>
                </PreferenceRadioEmail>
                <PreferenceRadioMail>
                  <label htmlFor="radioBatchCustomer">
                    <input
                      aria-label={`Preference for all other Customer communications Mail`}
                      type="radio"
                      name={'deliveryPref_customer_batch'}
                      value="CustomerBatch"
                      id="radioBatchCustomer"
                      onChange={() => setCustomerDeliveryPreference('Batch')}
                      checked={customerDeliveryPreference === 'Batch'}
                      disabled={isLoading || !editingDeliveryPreferences}
                    />{' '}
                    Mail
                  </label>
                </PreferenceRadioMail>
              </ProfileRadioButtonContainer>
            </ProfileEditorsContainer>
          </GoPaperlessPolicyPreferenceContainer>
          {!editingDeliveryPreferences && (
            <EditDeliveryPreferencesButton
              onClick={() => {
                setEditingDeliveryPreferences(!editingDeliveryPreferences);
              }}
            >
              Edit
            </EditDeliveryPreferencesButton>
          )}
          {editingDeliveryPreferences && (
            <SaveCancelContainer>
              <CancelButton
                onClick={() => {
                  setEditingDeliveryPreferences(!editingDeliveryPreferences);
                  setMappedPolicyPreference(policiesMapped);
                  setCustomerDeliveryPreference(customer.preferredDeliveryMethod);
                }}
              >
                Cancel
              </CancelButton>
              <SaveDeliveryPreferencesButton
                onClick={() => {
                  setEditingDeliveryPreferences(!editingDeliveryPreferences);
                  updateDeliveryPreferences();
                }}
                disabled={checkDisabled()}
              >
                Apply
              </SaveDeliveryPreferencesButton>
            </SaveCancelContainer>
          )}
        </>
      )}
    </GoPaperlessContainer>
  );
};

DeliveryPreferences.propTypes = {
  dispatch: PropTypes.func,
  i18n: PropTypes.object,
  isLoading: PropTypes.bool,
  customer: PropTypes.object,
  emailAddress: PropTypes.string,
  formErrors: PropTypes.string,
  profileName: PropTypes.string,
  profileMailingAddress: PropTypes.object,
  phoneNumberPrimary: PropTypes.string,
  phoneNumberSecondary: PropTypes.string,
  phoneNumberPrimaryName: PropTypes.object,
  phoneNumberSecondaryName: PropTypes.object,
  phoneErrors: PropTypes.any,
  emailErrors: PropTypes.any,
  editingEmailAndPhone: PropTypes.bool,
  primaryPhoneNameType: PropTypes.string,
  secondaryPhoneNameType: PropTypes.string,
  state: PropTypes.object,
  customerParty: PropTypes.object,
};
const mapStateToProps = (state) => ({
  i18n: state.i18nSlice.i18n,
  isLoading: state.deliveryPreferencesSlice.isLoading,
  policies: state.policySlice.policies,
  statementAccounts: state.deliveryPreferencesSlice.statementAccounts,
  systemId: state.customerSlice.customer.systemId,
  customer: state.customerSlice.customer, //do not remove: used in patch customer
  billingAccounts: state.billingSlice.billingAccounts,
  customerAddress: getMailingAddress(state),
  customerParty: getCustomerParty(state),
  customerEmail: getCustomerParty(state).emailInfo.emailAddr,
  customerId: state.loginSlice.user.customer_id,
  desktopDeliveryPreferencesDefaultExpanded:
    state.configurationSlice.features.features.desktopDeliveryPreferencesDefaultExpanded,
  createdSinceDate: state.configurationSlice.features.policyCreatedSinceDt,
  mobileDeliveryPreferencesDefaultExpanded:
    state.configurationSlice.features.features.mobileDeliveryPreferencesDefaultExpanded,
  policyStatusCd: state.configurationSlice.features.policyStatusCd,
  policiesCountLimit: state.configurationSlice.features.policiesCountLimit,
});

export default connect(mapStateToProps)(DeliveryPreferences);
